import * as React from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import {
  ChartBarIcon,
  ChatIcon,
  CogIcon,
  CollectionIcon,
  MailIcon,
  LogoutIcon,
  MoonIcon,
  OfficeBuildingIcon,
  QuestionMarkCircleIcon,
  SunIcon,
  SwitchHorizontalIcon,
  UserGroupIcon,
} from "@heroicons/react/outline";
import { useLocation, useNavigate } from "react-router-dom";

import { changeProfileImage } from "../../../../api/profile/changeProfileImage";
import { updateProfileMe } from "../../../../api/profile/updateProfileMe";
import { useAuth } from "../../../../app/providers/AuthProvider";
import useAccountSettingsStore from "../../../../features/settings/store/useAccountSettingsStore";
import use123zapStore from "../../../../stores/123zapStore";
import logoMainDark123Zap from "../../../../shared/assets/brand/logo-main-dark-123zap.png";
import {
  AccountMenuHeader,
  ACCOUNT_MENU_DIVIDER_SX,
  ACCOUNT_MENU_ITEM_SX,
  ACCOUNT_MENU_LIST_SX,
  ACCOUNT_MENU_PAPER_SX,
  ACCOUNT_MENU_WIDTH,
  UserAvatarCustomizerDialog,
  UserProfileDialog,
  resolveAccountMenuIdentity,
} from "../../../../shared/components/product";
import { ActionMenu } from "../../../../shared/components/ui";
import {
  canAccessPermission,
  isFeatureAllowed,
} from "../../../../shared/lib/access/featureAccess";
import { useAccountShellHydration } from "../../../../features/settings/hooks/useAccountShellHydration";
import { getHasMultipleAccounts } from "../../../../shared/lib/auth/accountContext";
import {
  applyAvatarResponseToUser,
  avatarConfigToPngDataUrl,
  buildUserAvatarUrl,
  imageDataUrlToPngDataUrl,
  subscribeToAvatarChanges,
} from "../../../../shared/lib/userAvatar";
import {
  SIDEBAR_COLOR_MODE,
  setStoredSidebarDarkMode,
  useSidebarDarkMode,
} from "../../useAppLayoutAppearance";
import { APP_SHELL, getPrivateShellColors } from "./MenuContent";
import { SHOW_ATTENDANCE_NAVIGATION } from "./navigationFlags";
import SideMenuMobile from "./SideMenuMobile";

const SHELL_COLOR_TRANSITION =
  "background-color 180ms ease, border-color 180ms ease, color 180ms ease";
const TOPBAR_ICON_STYLE = { flexShrink: 0, height: 22, width: 22 };
const MENU_ICON_STYLE = { flexShrink: 0, height: 22, width: 22 };
const getTopbarSubmenuTextColor = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.text.primary : "rgb(30, 41, 59)";
const getTopbarSubmenuIconColor = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.text.secondary : "rgb(100, 116, 139)";
const getTopbarSubmenuHoverBg = (theme) =>
  theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.14)" : "rgba(148, 163, 184, 0.12)";
const TOPBAR_SUBMENU_PAPER_SX = {
  backgroundColor: (theme) => theme.palette.background.paper,
  border: (theme) => `1px solid ${theme.palette.border.default}`,
  borderRadius: "4px",
  boxShadow:
    "0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)",
  color: getTopbarSubmenuTextColor,
  mt: -0.625,
  overflow: "hidden",
};
const TOPBAR_SUBMENU_LIST_SX = {
  py: 0,
};
const TOPBAR_SUBMENU_ITEM_SX = {
  alignItems: "center",
  color: getTopbarSubmenuTextColor,
  display: "flex",
  fontSize: 13,
  fontWeight: 500,
  gap: 1.5,
  height: 43,
  lineHeight: "normal",
  minHeight: 43,
  px: 2,
  py: 0,
  "& > [aria-hidden='true']": {
    color: getTopbarSubmenuIconColor,
  },
  "&:hover": {
    backgroundColor: getTopbarSubmenuHoverBg,
  },
  "&.Mui-focusVisible": {
    backgroundColor: getTopbarSubmenuHoverBg,
  },
};
const topbarNavigationItems = [
  {
    label: "Relatórios",
    href: "/painel",
    icon: ChartBarIcon,
  },
  {
    label: "Atendimentos",
    href: "/atendimentos",
    icon: ChatIcon,
    hidden: !SHOW_ATTENDANCE_NAVIGATION,
    access: {
      module: "crm.attendance",
    },
  },
  {
    label: "Canais",
    href: "/canais",
    icon: SwitchHorizontalIcon,
    access: {
      action: "use",
      module: "channels",
    },
  },
  {
    label: "Públicos",
    href: "/contatos",
    icon: WorkspacesOutlinedIcon,
    access: {
      action: "use",
      module: "audiences",
    },
    children: [
      {
        label: "Contatos",
        href: "/contatos",
        icon: AssignmentIndOutlinedIcon,
      },
      {
        label: "Listagens",
        href: "/listagens",
        icon: CollectionIcon,
      },
    ],
  },
  {
    label: "Modelos",
    href: "/modelos",
    icon: MailIcon,
    access: {
      action: "use",
      module: "message_templates",
    },
  },
  {
    label: "Campanhas",
    href: "/campanhas",
    icon: RocketLaunchOutlinedIcon,
    access: {
      action: "view",
      module: "campaigns",
    },
  },
  {
    label: "Ajustes",
    href: "/ajustes/empresa",
    icon: CogIcon,
    children: [
      {
        label: "Conta",
        href: "/ajustes/empresa",
        activeMatch: (pathname) =>
          pathname === "/ajustes" ||
          pathname === "/ajustes/empresa" ||
          pathname.startsWith("/ajustes/empresa/") ||
          pathname === "/ajustes/conta" ||
          pathname.startsWith("/ajustes/conta/"),
        icon: OfficeBuildingIcon,
        access: {
          action: "use",
          module: "account_settings",
        },
      },
      {
        label: "Usuários",
        href: "/ajustes/usuarios",
        icon: UserGroupIcon,
        access: {
          action: "use",
          module: "account_users",
        },
      },
      {
        label: "Funcionalidades",
        href: "/ajustes/funcionalidades",
        icon: AppsOutlinedIcon,
        access: {
          action: "use",
          module: "account_features",
        },
      },
      {
        label: "Integrações",
        href: "/ajustes/integracoes",
        icon: ShareOutlinedIcon,
        access: {
          action: "use",
          module: "integrations",
        },
      },
    ],
  },
];

function openDocumentation() {
  window.open("https://123zap.gitbook.io/docs", "_blank", "noopener,noreferrer");
}

function isTopbarNavItemActive(pathname, item) {
  if (Array.isArray(item.children)) {
    return item.children.some((child) => isTopbarNavItemActive(pathname, child));
  }

  if (typeof item.activeMatch === "function") {
    return item.activeMatch(pathname);
  }

  const activePath = item.activePrefix || item.href;
  return pathname === activePath || pathname.startsWith(`${activePath}/`);
}

function isNavigationItemAllowed(item, user, featureAccess) {
  if (item?.hidden) {
    return false;
  }

  if (item?.access?.module && !isFeatureAllowed(featureAccess, item.access.module)) {
    return false;
  }

  if (item?.access?.action) {
    return canAccessPermission(user, item.access.module, item.access.action);
  }

  return true;
}

function getVisibleNavigationItems(items, user, featureAccess) {
  return items
    .map((item) => {
      if (!isNavigationItemAllowed(item, user, featureAccess)) return null;

      if (!Array.isArray(item.children)) return item;

      const children = getVisibleNavigationItems(item.children, user, featureAccess);
      return children.length ? { ...item, children } : null;
    })
    .filter(Boolean);
}

function TopbarNavItem({ active, colors, item, mobile = false, onClick, pathname }) {
  const Icon = item.icon;
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const itemSx = (menuOpen = false) => (theme) => ({
    alignItems: "center",
    backgroundColor: menuOpen ? "rgba(148, 163, 184, 0.12)" : "transparent",
    borderRadius: "4px",
    color: menuOpen ? colors.text : active ? colors.activeText : colors.navText,
    display: "flex",
    flexShrink: 0,
    gap: mobile ? 0.8 : 1.5,
    height: mobile ? 34 : 43,
    px: mobile ? 1.1 : 2,
    transition: SHELL_COLOR_TRANSITION,
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: colors.text,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.border.focus}`,
      outlineOffset: 2,
    },
  });
  const label = (
    <Typography
      component="span"
      sx={{
        color: "currentColor",
        fontSize: mobile ? 12 : 13,
        fontWeight: 500,
        lineHeight: mobile ? "16px" : "18px",
      }}
    >
      {item.label}
    </Typography>
  );

  if (hasChildren) {
    return (
      <ActionMenu
        ariaLabel={`Abrir menu ${item.label}`}
        menuItemSx={TOPBAR_SUBMENU_ITEM_SX}
        menuListSx={TOPBAR_SUBMENU_LIST_SX}
        menuPaperSx={TOPBAR_SUBMENU_PAPER_SX}
        minWidth={194}
        placement="bottom-start"
        actions={item.children.map((child) => {
          const childActive = isTopbarNavItemActive(pathname, child);
          const ChildIcon = child.icon;

          return {
            id: child.href,
            icon: ChildIcon ? <ChildIcon aria-hidden style={MENU_ICON_STYLE} /> : null,
            iconPosition: "start",
            label: child.label,
            onClick: () => onClick(child),
            sx: {
              backgroundColor: childActive ? getTopbarSubmenuHoverBg : undefined,
              color: getTopbarSubmenuTextColor,
              "& > [aria-hidden='true']": {
                color: childActive ? getTopbarSubmenuTextColor : getTopbarSubmenuIconColor,
              },
            },
          };
        })}
        renderTrigger={({ buttonProps, open }) => (
          <ButtonBase
            {...buttonProps}
            aria-current={active ? "page" : undefined}
            sx={itemSx(open)}
          >
            {mobile ? null : (
              <Icon
                aria-hidden
                style={{
                  ...TOPBAR_ICON_STYLE,
                  color: open ? colors.muted : undefined,
                }}
              />
            )}
            {label}
          </ButtonBase>
        )}
      />
    );
  }

  return (
    <ButtonBase
      type="button"
      aria-current={active ? "page" : undefined}
      aria-label={item.label}
      onClick={() => onClick(item)}
      sx={itemSx()}
    >
      {mobile ? null : <Icon aria-hidden style={TOPBAR_ICON_STYLE} />}
      {label}
    </ButtonBase>
  );
}

function TopbarIconButton({ children, colors, label, onClick }) {
  return (
    <Tooltip title={label}>
      <ButtonBase
        type="button"
        aria-label={label}
        onClick={onClick}
        sx={(theme) => ({
          alignItems: "center",
          borderRadius: "50%",
          color: colors.text,
          display: "flex",
          flexShrink: 0,
          height: 36,
          justifyContent: "center",
          transition: SHELL_COLOR_TRANSITION,
          width: 36,
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
            color: colors.text,
          },
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.border.focus}`,
            outlineOffset: 2,
          },
        })}
      >
        {children}
      </ButtonBase>
    </Tooltip>
  );
}

function AccountMenu({ colors }) {
  const navigate = useNavigate();
  const { logout, updateAuthenticatedUser, user } = useAuth();
  const accountState = useAccountSettingsStore((state) => state.accountState);
  const [avatarDialogOpen, setAvatarDialogOpen] = React.useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = React.useState(false);
  const [avatarSaving, setAvatarSaving] = React.useState(false);
  const [profileSaving, setProfileSaving] = React.useState(false);
  const [avatarVersion, setAvatarVersion] = React.useState(0);
  const initial = (user?.name || user?.email || "Z").trim().charAt(0).toUpperCase();
  const avatarUrl = buildUserAvatarUrl(user);
  const accountIdentity = resolveAccountMenuIdentity({ accountState, user });
  const accountName = accountIdentity.name;
  const accountLogoUrl = accountIdentity.logoUrl;
  const accountInitial = accountIdentity.initial;
  const canSwitchAccount =
    Number(user?.accountsCount || 0) > 1 ||
    getHasMultipleAccounts(user?.email);

  React.useEffect(() => subscribeToAvatarChanges(() => {
    setAvatarVersion((current) => current + 1);
  }), []);

  const persistAvatar = React.useCallback(async (base64Image, avatarConfig) => {
    setAvatarSaving(true);

    try {
      const response = await changeProfileImage(base64Image, avatarConfig);
      const nextUser = applyAvatarResponseToUser(user, response, avatarConfig);

      updateAuthenticatedUser(nextUser);
      setAvatarVersion((current) => current + 1);
    } finally {
      setAvatarSaving(false);
    }
  }, [updateAuthenticatedUser, user]);

  const handleAvatarPhotoUpload = React.useCallback(async (dataUrl) => {
    const pngDataUrl = await imageDataUrlToPngDataUrl(dataUrl);
    await persistAvatar(pngDataUrl);
  }, [persistAvatar]);

  const handleAvatarConfigSave = React.useCallback(async (config) => {
    const pngDataUrl = await avatarConfigToPngDataUrl(config, user);
    await persistAvatar(pngDataUrl, config);
  }, [persistAvatar, user]);

  const handleProfileSave = React.useCallback(async (profileData) => {
    setProfileSaving(true);

    try {
      const response = await updateProfileMe({
        name: String(profileData?.name || "").trim(),
        nickname: String(profileData?.nickname || "").trim(),
      });
      const updatedUser = response?.user || {};

      updateAuthenticatedUser({
        ...user,
        name: updatedUser.name || updatedUser.display_name || profileData?.name || user?.name,
        nickname: updatedUser.nickname || profileData?.nickname || "",
      });
    } finally {
      setProfileSaving(false);
    }
  }, [updateAuthenticatedUser, user]);

  return (
    <>
      <ActionMenu
        ariaLabel="Abrir opções da conta"
        menuItemSx={ACCOUNT_MENU_ITEM_SX}
        menuListSx={ACCOUNT_MENU_LIST_SX}
        menuHeader={({ closeMenu }) => (
          <AccountMenuHeader
            avatarUrl={avatarUrl}
            avatarVersion={avatarVersion}
            email={user?.email}
            initial={initial}
            onEditProfile={() => {
              closeMenu();
              setProfileDialogOpen(true);
            }}
            productName={accountName}
            userName={user?.name || user?.username}
          />
        )}
        menuPaperSx={ACCOUNT_MENU_PAPER_SX}
        minWidth={ACCOUNT_MENU_WIDTH}
        placement="bottom-end"
        actions={[
          {
            id: "switch-account",
            icon: <SwitchHorizontalIcon aria-hidden height={24} width={24} />,
            label: "Trocar conta",
            hidden: !canSwitchAccount,
            iconPosition: "start",
            onClick: () => {
              navigate("/selecionar-conta?modo=trocar", {
                state: { mode: "switch-account" },
              });
            },
          },
          {
            id: "account-actions-divider",
            type: "divider",
            hidden: !canSwitchAccount,
            sx: ACCOUNT_MENU_DIVIDER_SX,
          },
          {
            id: "logout",
            icon: <LogoutIcon aria-hidden height={24} width={24} />,
            label: "Sair",
            iconPosition: "start",
            onClick: async () => {
              await logout();
            },
          },
        ]}
        renderTrigger={({ buttonProps }) => (
          <Tooltip title="Opções da conta">
            <ButtonBase
              {...buttonProps}
              sx={(theme) => ({
                alignItems: "center",
                border: 0,
                borderRadius: "50%",
                color: colors.text,
                display: "flex",
                flexShrink: 0,
                height: 36,
                justifyContent: "center",
                p: 0,
                transition: SHELL_COLOR_TRANSITION,
                width: 36,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.border.focus}`,
                  outlineOffset: 2,
                },
              })}
            >
              <Box
                sx={{
                  alignItems: "center",
                  backgroundColor: colors.activeBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "50%",
                  color: colors.activeText,
                  display: "flex",
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 800,
                  height: 30,
                  justifyContent: "center",
                  overflow: "hidden",
                  width: 30,
                }}
              >
                {accountLogoUrl ? (
                  <Box
                    component="img"
                    src={accountLogoUrl}
                    alt=""
                    sx={{ height: "100%", objectFit: "cover", width: "100%" }}
                  />
                ) : (
                  accountInitial
                )}
              </Box>
            </ButtonBase>
          </Tooltip>
        )}
      />
      <UserAvatarCustomizerDialog
        open={avatarDialogOpen}
        onClose={() => setAvatarDialogOpen(false)}
        onSaveAvatar={handleAvatarConfigSave}
        saving={avatarSaving}
        user={user}
      />
      <UserProfileDialog
        avatarSaving={avatarSaving}
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        onCustomizeAvatar={() => setAvatarDialogOpen(true)}
        onSaveProfile={handleProfileSave}
        onUploadAvatarPhoto={handleAvatarPhotoUpload}
        profileSaving={profileSaving}
        user={user}
      />
    </>
  );
}

function AppDesktopTopbar({ colors, sidebarDark }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const accountProfile = useAccountSettingsStore(
    (state) => state.accountState.profile,
  );
  const featureAccess = use123zapStore((state) => state.featureAccess);
  const featureAccessAccountId = use123zapStore((state) => state.featureAccessAccountId);
  const featureAccessLoaded = use123zapStore((state) => state.featureAccessLoaded);
  const featureAccessLoading = use123zapStore((state) => state.featureAccessLoading);
  const featureAccessReady = Boolean(
    !user?.accountId ||
    (
      featureAccessLoaded &&
      !featureAccessLoading &&
      featureAccess &&
      featureAccessAccountId === user.accountId
    ),
  );
  const effectiveFeatureAccess = featureAccessReady ? featureAccess : null;
  const accountProfileId =
    accountProfile?.account_id ||
    accountProfile?.accountId ||
    accountProfile?.id ||
    "";
  const accountPermissions =
    accountProfileId && accountProfileId === user?.accountId
      ? accountProfile?.permissions
      : null;
  const permissionUser = React.useMemo(
    () => (
      accountPermissions
        ? { ...user, permissions: accountPermissions }
        : user
    ),
    [accountPermissions, user],
  );
  const navigationItems = React.useMemo(
    () => (
      featureAccessReady
        ? getVisibleNavigationItems(topbarNavigationItems, permissionUser, effectiveFeatureAccess)
        : []
    ),
    [effectiveFeatureAccess, featureAccessReady, permissionUser],
  );

  return (
    <Box
      component="header"
      sx={{
        alignItems: "center",
        backgroundColor: colors.surface,
        borderBottom: `1px solid ${colors.border}`,
        boxSizing: "border-box",
        display: { xs: "none", md: "flex" },
        flexShrink: 0,
        height: APP_SHELL.topbarHeight,
        minWidth: 0,
        px: { md: 3, lg: 4.5 },
        transition: SHELL_COLOR_TRANSITION,
        width: "100%",
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <ButtonBase
        type="button"
        aria-label="Ir para o painel"
        onClick={() => navigate("/painel")}
        sx={{
          borderRadius: 1,
          display: "flex",
          flexShrink: 0,
          mr: { md: 3, lg: 3.5 },
          p: 0.25,
          "&:focus-visible": {
            outline: (theme) => `2px solid ${theme.palette.border.focus}`,
            outlineOffset: 2,
          },
        }}
      >
        <Box
          component="img"
          src={logoMainDark123Zap}
          alt="123zap"
          sx={{
            display: "block",
            height: 30,
            objectFit: "contain",
            objectPosition: "left center",
            width: "auto",
          }}
        />
      </ButtonBase>

      <Stack
        component="nav"
        direction="row"
        spacing={0.25}
        sx={{
          alignItems: "center",
          flex: "1 1 auto",
          minWidth: 0,
          overflowX: "auto",
          overflowY: "hidden",
          pr: 1,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {navigationItems.map((item) => (
          <TopbarNavItem
            active={isTopbarNavItemActive(pathname, item)}
            colors={colors}
            item={item}
            key={item.href}
            onClick={(target = item) => navigate(target.href)}
            pathname={pathname}
          />
        ))}
      </Stack>

      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: "center",
          flexShrink: 0,
          ml: 1,
        }}
      >
        <TopbarIconButton colors={colors} label="Documentação" onClick={openDocumentation}>
          <QuestionMarkCircleIcon aria-hidden height={22} width={22} />
        </TopbarIconButton>
        <TopbarIconButton
          colors={colors}
          label={sidebarDark ? "Usar tema claro" : "Usar tema escuro"}
          onClick={() => {
            if (!SIDEBAR_COLOR_MODE.toggleEnabled) return;
            setStoredSidebarDarkMode(!sidebarDark);
          }}
        >
          {sidebarDark ? (
            <SunIcon aria-hidden height={22} width={22} />
          ) : (
            <MoonIcon aria-hidden height={22} width={22} />
          )}
        </TopbarIconButton>
        <AccountMenu colors={colors} />
      </Stack>
    </Box>
  );
}

function AppMobileTopbar({ colors, onOpen }) {
  const navigate = useNavigate();

  return (
    <Box
      component="header"
      sx={(theme) => ({
        alignItems: "center",
        backgroundColor: colors.surface,
        borderBottom: `1px solid ${colors.border}`,
        boxSizing: "border-box",
        display: { xs: "flex", md: "none" },
        height: APP_SHELL.mobileHeaderHeight,
        left: 0,
        px: 2,
        position: "fixed",
        right: 0,
        top: "var(--template-frame-height, 0px)",
        transition: SHELL_COLOR_TRANSITION,
        zIndex: theme.zIndex.appBar,
      })}
    >
      <ButtonBase
        type="button"
        aria-label="Ir para o painel"
        onClick={() => navigate("/painel")}
        sx={{
          borderRadius: 1,
          display: "flex",
          flexShrink: 0,
          p: 0.25,
          "&:focus-visible": {
            outline: (theme) => `2px solid ${theme.palette.border.focus}`,
            outlineOffset: 2,
          },
        }}
      >
        <Box
          component="img"
          src={logoMainDark123Zap}
          alt="123zap"
          sx={{
            display: "block",
            height: 28,
            maxWidth: 116,
            objectFit: "contain",
            objectPosition: "left center",
            width: "auto",
          }}
        />
      </ButtonBase>

      <ButtonBase
        type="button"
        aria-label="Abrir menu"
        onClick={onOpen}
        sx={(theme) => ({
          alignItems: "center",
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          color: colors.muted,
          display: "flex",
          height: 36,
          justifyContent: "center",
          ml: "auto",
          transition: SHELL_COLOR_TRANSITION,
          width: 40,
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
            color: colors.text,
          },
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.border.focus}`,
            outlineOffset: 2,
          },
        })}
      >
        <MenuRoundedIcon sx={{ fontSize: 20 }} />
      </ButtonBase>
    </Box>
  );
}

export default function AppNavbar() {
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);
  const theme = useTheme();
  const { pathname } = useLocation();
  const sidebarDark = useSidebarDarkMode();
  const colors = getPrivateShellColors(theme, sidebarDark);
  useAccountShellHydration({ enabled: pathname !== "/ajustes/empresa" });
  const toggleMobileDrawer = (open) => () => {
    setMobileDrawerOpen(open);
  };

  return (
    <>
      <AppDesktopTopbar colors={colors} sidebarDark={sidebarDark} />
      <AppMobileTopbar colors={colors} onOpen={toggleMobileDrawer(true)} />
      <SideMenuMobile open={mobileDrawerOpen} toggleDrawer={toggleMobileDrawer} />
    </>
  );
}
